import React from 'react';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSectionHint } from '../../lib/hooks/useSectionHint';
import type { SectionKey } from '../../lib/config/sectionDiscoveryHints';

interface SectionDiscoveryBannerProps {
  section: SectionKey;
  count: number;
}

/**
 * Suggerimento in testa a una sezione durante l'onboarding.
 *
 * Registro preso da SetupFloatingBubble, che è la superficie Notion di
 * riferimento per l'onboarding: card piatta con bordo #EBEBEA, occhiello in
 * maiuscoletto, testo 14/12.5. Il brand compare come pieno piatto sul bottone
 * e come pallino da 8px: niente gradienti di riempimento, niente fondo colorato
 * e niente riquadro icona, che facevano sembrare l'avviso una pubblicità
 * incollata sopra la pagina.
 *
 * Le due varianti si distinguono per parole e per peso del bottone, non per
 * colore: `discovery` è la prima azione da fare, `advanced` è un consiglio a
 * lavoro avviato e resta secondario.
 */
export function SectionDiscoveryBanner({ section, count }: SectionDiscoveryBannerProps) {
  const navigate = useNavigate();
  const { variant, copy, onDismiss, onCtaClick } = useSectionHint(section, count);

  if (!variant || !copy) return null;

  const isDiscovery = variant === 'discovery';

  const primaryBtnCls = isDiscovery
    ? 'text-white bg-[#E91E8C] hover:bg-[#C9187A]'
    : 'text-[#37352F] dark:text-white/85 border border-[#EBEBEA] dark:border-white/15 hover:bg-[#FAFAFA] dark:hover:bg-white/5';

  const handlePrimaryClick = () => {
    onCtaClick('primary');
    if (copy.cta?.href) navigate(copy.cta.href);
  };

  const handleSecondaryClick = () => {
    onCtaClick('secondary');
    if (copy.secondaryCta?.href) navigate(copy.secondaryCta.href);
  };

  const Icon = copy.icon;

  return (
    <div className="relative rounded-lg border border-[#EBEBEA] dark:border-white/10 bg-white dark:bg-[#202022] px-4 py-3.5 mb-4">
      <button
        onClick={() => void onDismiss()}
        aria-label="Chiudi suggerimento"
        className="absolute top-2.5 right-2.5 p-1 rounded-md text-[#9B9A97] hover:text-[#37352F] dark:hover:text-white hover:bg-[#FAFAFA] dark:hover:bg-white/5 transition-colors"
      >
        <X className="h-3.5 w-3.5" />
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 pr-6">
        <Icon className="shrink-0 self-start sm:self-center w-4 h-4 text-[#9B9A97] dark:text-white/40" />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            {isDiscovery && (
              <span className="shrink-0 w-2 h-2 rounded-full bg-[#E91E8C]" />
            )}
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#787774] dark:text-white/50">
              {copy.badge}
            </span>
          </div>
          <h3 className="text-[14px] font-semibold text-[#37352F] dark:text-white leading-tight mb-0.5">
            {copy.title}
          </h3>
          <p className="text-[12.5px] text-[#787774] dark:text-white/50 leading-snug">
            {copy.body}
          </p>
        </div>

        {(copy.cta || copy.secondaryCta) && (
          <div className="shrink-0 flex items-center gap-1 self-start sm:self-center">
            {copy.cta && (
              <button
                onClick={handlePrimaryClick}
                className={`px-3 py-1.5 rounded-md text-[12px] font-medium whitespace-nowrap transition-all ${primaryBtnCls}`}
              >
                {copy.cta.label}
              </button>
            )}
            {copy.secondaryCta && (
              <button
                onClick={handleSecondaryClick}
                className="px-2 py-1.5 rounded-md text-[11.5px] text-[#787774] hover:text-[#37352F] dark:hover:text-white whitespace-nowrap transition-colors"
              >
                {copy.secondaryCta.label}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
